import "./index.css"

import { useId } from "@utilities/useId"
import classNames from "classnames"
import React, { forwardRef, InputHTMLAttributes } from "react"

export type ToggleSwitchSize = "md" | "sm"

interface ToggleSwitchBaseProps {
  "aria-invalid"?: never
  "aria-label"?: never
  "aria-required"?: never
  /** Optional description text that appears below the toggle switch label. */
  description?: string
  required?: never
  /** The size of the toggle switch. */
  size?: ToggleSwitchSize
}

interface ToggleSwitchWithVisibleLabel extends ToggleSwitchBaseProps {
  "aria-labelledby"?: never
  /** When true, hides the visible label and sets it as aria-label on the input instead. */
  hideLabel?: false
  /**
   * The label content for the toggle switch. Required unless
   * `aria-labelledby` is provided to reference an external label. When
   * using non-string content (e.g. JSX), it must not contain
   * interactive elements. This includes native HTML elements such as
   * `<a>` and `<button>`, as well as Tapestry or Tapestry-React
   * interactive components.
   */
  label: React.ReactNode
}

interface ToggleSwitchWithHiddenLabel extends ToggleSwitchBaseProps {
  "aria-labelledby"?: never
  /** When true, hides the visible label and sets it as aria-label on the input instead. */
  hideLabel: true
  /** Label text for the toggle switch, used as an accessible `aria-label`. */
  label: string
}

interface ToggleSwitchWithAriaLabelledBy extends ToggleSwitchBaseProps {
  "aria-labelledby": string
  hideLabel?: never
  /** Not available when `aria-labelledby` is provided. */
  label?: never
}

export type ToggleSwitchProps =
  | ToggleSwitchWithVisibleLabel
  | ToggleSwitchWithHiddenLabel
  | ToggleSwitchWithAriaLabelledBy

export type ToggleSwitchElementProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  keyof ToggleSwitchProps | "type" | "required" | "aria-required"
> &
  ToggleSwitchProps

/**
 * A toggle switch component that renders as a checkbox input styled as a switch.
 *
 * **Required:** You must provide either:
 * - `label` - Label content for the switch (must be a string when `hideLabel` is true)
 * - `aria-labelledby` - ID of an external element that provides the label
 *
 * When using non-string content for `label` (e.g. JSX), it must not contain
 * interactive elements. This includes native HTML elements such as `<a>` and
 * `<button>`, as well as Tapestry or Tapestry-React interactive components.
 *
 * @component
 * @see {@link https://planningcenter.github.io/tapestry/?path=/docs/components-toggle-switch--docs | Storybook Documentation}
 */
export const ToggleSwitch = forwardRef<
  HTMLInputElement,
  ToggleSwitchElementProps
>(
  (
    {
      "aria-describedby": ariaDescribedby,
      checked,
      className,
      description,
      hideLabel = false,
      id,
      label,
      size = "md",
      ...restProps
    }: ToggleSwitchElementProps,
    ref
  ) => {
    const combinedClassName = classNames(
      "tds-toggle-switch",
      size && size === "sm" && `tds-toggle-switch--sm`,
      hideLabel && `tds-toggle-switch--hide-label`,
      className
    )

    const stableId = useId()
    const toggleSwitchId = id || `tds-toggle-switch-${stableId}`

    return (
      <div className={combinedClassName}>
        <div className="tds-toggle-switch-track" aria-hidden="true"></div>
        <input
          {...restProps}
          id={toggleSwitchId}
          type="checkbox"
          role="switch"
          aria-checked={checked}
          aria-label={hideLabel ? (label as string) : undefined}
          aria-describedby={
            description && !hideLabel
              ? `${toggleSwitchId}-description`
              : ariaDescribedby
          }
          checked={checked}
          ref={ref}
        />
        {label && !hideLabel && <label htmlFor={toggleSwitchId}>{label}</label>}
        {description && !hideLabel && (
          <p
            id={`${toggleSwitchId}-description`}
            className="tds-toggle-switch-description"
          >
            {description}
          </p>
        )}
      </div>
    )
  }
)

ToggleSwitch.displayName = "ToggleSwitch"
